import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { api360 } from "./api";
import {
  ActivityLogPayload,
  ContentHubTrendReportPayload,
  GetHomeLeadsPayload,
  GetLeadDetailPayload,
  ImportLeadsPayload,
  LeadsImportHistorySearchPayload,
  LeadsSearchPayload,
  LeadsSummaryPayload,
  LeadsTemplatePayload,
  SearchReq,
  UpdateLeadDetailPayload,
} from "../types/leads.types";

export const getLeadSummary = (
  data: LeadsSummaryPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/summary";
  return api360.post(url, data);
};
export const getLeadPipelines = (data: SearchReq): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/pipeline";
  return api360.post(url, data);
};
export const getLeadDistribution = (
  data: SearchReq,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/distribution";
  return api360.post(url, data);
};

export const getMetricsFromAssignedChannel = (
  data: LeadsSummaryPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/metrics/assignedchannel";
  return api360.post(url, data);
};

export const getMetricsFromSourceChannel = (
  data: LeadsSummaryPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/metrics/sourcechannel";
  return api360.post(url, data);
};

export const getAccumulatedChartData = (
  data: LeadsSummaryPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/accumulation";
  return api360.post(url, data);
};

export const getSourceChannelOptions = (
  data: SearchReq,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/options/channels";
  return api360.post(url, data);
};

export const getSourceChannelList = (
  data: SearchReq,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/options/getSourceChannelList";

  return api360.post(url, data);
};

export const getLeadTemplate = (
  data: LeadsTemplatePayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/getTemplate";

  return api360.post(url, data);
};

export const importLeads = (
  data: ImportLeadsPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/import";

  const formData = new FormData();
  formData.append("userName", data.userName);
  formData.append("channelName", data.channelName);
  formData.append("permission_Token", data.permission_Token);
  formData.append("file", data.file, data.file.name);

  return api360.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getAssignedChannelOptions = (
  data: SearchReq,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/options/assignedchannels";
  return api360.post(url, data);
};

export const getStatusOptions = (data: SearchReq): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/options/statuses";
  return api360.post(url, data);
};

export const getSearchLeads = (
  data: LeadsSearchPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/search";
  return api360.post(url, data);
};

export const getImportHistoryList = (
  data: LeadsImportHistorySearchPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/getImportHistoryList";
  return api360.post(url, data);
};

export const getCustomerStatusOptions = (
  data: SearchReq,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/options/customerresponse";
  return api360.post(url, data);
};

export const getLeadDetails = (
  data: GetLeadDetailPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/detail";
  return api360.post(url, data);
};

export const updateLeadDetails = (
  data: UpdateLeadDetailPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/edit-detail";
  return api360.post(url, data);
};

export const getUserInterestsData = (data: GetLeadDetailPayload): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/user-interests-data";
  return api360.post(url, data);
}

export const getActivityLog = (
  data: ActivityLogPayload,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/activity-log";
  return api360.post(url, data);
};

export const getWeeklyOrMonthlySummary = (
  data: GetHomeLeadsPayload,
): Promise<AxiosResponse> => {
  const isWeekly = data.frequency === "weekly";

  const url = "/c360/v1/api/lead/homepage";
  return api360.post(url, data);
};

export const getSocialInsightsData = (
  data: SearchReq,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/getsocialinsightsdata";
  return api360.post(url, data);
};

export const useGetSourceChannelOptions = (data: SearchReq) => {
  return useQuery({
    queryKey: ["ChannelOptions"],
    queryFn: () => getSourceChannelOptions(data),
  });
};

export const useGetSourceChannelList = (data: SearchReq) => {
  return useQuery({
    queryKey: ["ChannelList"],
    queryFn: () => getSourceChannelList(data),
  });
};

export const useGetLeadTemplate = (
  payload: LeadsTemplatePayload,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["leadTemplate", payload.channelName, payload.permission_Token],
    queryFn: () => getLeadTemplate(payload),
    enabled: options?.enabled ?? false, // Prevent auto-fetch on mount
    refetchOnWindowFocus: false, // Avoid unnecessary refetch
  });
};

export const useImportLeadsApi = () => {
  return useMutation({
    mutationFn: importLeads,
  });
};

export const useGetAssignedChannelOptions = (data: SearchReq) => {
  return useQuery({
    queryKey: ["AssignedChannelOptions"],
    queryFn: () => getAssignedChannelOptions(data),
  });
};

export const useGetStatusOptions = (data: SearchReq) => {
  return useQuery({
    queryKey: ["StatusOptions", data.permission_Token],
    queryFn: () => getStatusOptions(data),
  });
};

export const useSearchLeads = () => {
  return useMutation({
    mutationFn: (data: LeadsSearchPayload) => getSearchLeads(data),
  });
};

export const useSearchLeadHistoryList = () => {
  return useMutation({
    mutationFn: (data: LeadsImportHistorySearchPayload) =>
      getImportHistoryList(data),
  });
};

export const useGetCustomerStatusOptions = (data: SearchReq) => {
  return useQuery({
    queryKey: ["CustomerStatusOptions"],
    queryFn: () => getCustomerStatusOptions(data),
  });
};

export const useGetLeadDetails = () => {
  return useMutation({
    mutationFn: (data: GetLeadDetailPayload) => getLeadDetails(data),
  });
};

export const useUpdateLeadDetails = () => {
  return useMutation({
    mutationFn: (data: UpdateLeadDetailPayload) => updateLeadDetails(data),
  });
};

export const useGetUserInterestsData = () => {
  return useMutation({
    mutationFn: (data: GetLeadDetailPayload) => getUserInterestsData(data),
  });
};

export const useGetActivityLog = () => {
  const mutation = useMutation({
    mutationFn: (data: ActivityLogPayload) => getActivityLog(data),
  });

  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isLoading: mutation.isLoading,
  };
  // return useQuery({
  //     queryKey: ["GetActivityLog"],
  //     queryFn: () => getActivityLog(data),
  // });
};

export const useGetHomepageLeadData = () => {
  const mutation = useMutation({
    mutationFn: (data: GetHomeLeadsPayload) => getWeeklyOrMonthlySummary(data),
  });

  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isLoading: mutation.isLoading,
  };
};

export const useGetLeadsSummary = () => {
  const mutation = useMutation({
    mutationFn: (data: LeadsSummaryPayload) => getLeadSummary(data),
  });

  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isLoading: mutation.isLoading,
  };
};
export const useGetLeadsPipeline = () => {
  const mutation = useMutation({
    mutationFn: (data: LeadsSummaryPayload) => getLeadPipelines(data),
  });

  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
  };
};

export const useGetDistribution = () => {
  const mutation = useMutation({
    mutationFn: (data: LeadsSummaryPayload) => getLeadDistribution(data),
  });

  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
  };
};

export const useGetMetricsFromAssignedChannel = () => {
  const mutation = useMutation({
    mutationFn: (data: LeadsSummaryPayload) =>
      getMetricsFromAssignedChannel(data),
  });

  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
  };
};

export const useGetMetricsFromSourceChannel = () => {
  const mutation = useMutation({
    mutationFn: (data: LeadsSummaryPayload) => {
      return getMetricsFromSourceChannel(data);
    },
  });
  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
  };
};

export const useGetAccumulatedChartData = () => {
  const mutation = useMutation({
    mutationFn: (data: LeadsSummaryPayload) => {
      return getAccumulatedChartData(data);
    },
  });

  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
  };
};

export const useGetSocialInsights = (data: SearchReq) => {
  return useQuery({
    queryKey: ["SocialInsights", data.permission_Token],
    queryFn: () => getSocialInsightsData(data),
  });
};

export const getCountryListOptions = (
  data: SearchReq,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/options/getcountrylist";
  return api360.post(url, data);
};

export const useGetCountryListOptions = (data: SearchReq) => {
  return useQuery({
    queryKey: ["CountryListOptions", data.permission_Token],
    queryFn: () => getCountryListOptions(data),
  });
};

export const getContentHubTrendReport = (
  data: ContentHubTrendReportPayload
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/lead/contenthub-trend-report";
  return api360.post(url, {
    permission_Token: data.permission_Token,
    start_date: data.start_date,
    end_date: data.end_date,
    group_criteria: data.group_criteria,
    country: data.country,
  });
};

export const useGetContentHubTrendReport = (
  payload: ContentHubTrendReportPayload,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [
      "ContentHubTrendReport",
      payload.permission_Token,
      payload.start_date,
      payload.end_date,
      payload.group_criteria,
      payload.country,
    ],
    queryFn: () =>
      getContentHubTrendReport({
        permission_Token: payload.permission_Token,
        start_date: payload.start_date,
        end_date: payload.end_date,
        group_criteria: payload.group_criteria,
        country: payload.country,
      }),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
  });
};
